'use client';
import { API_BASE_URL } from '@/lib/config';


import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ProductCard } from './components/ProductCard';
import { LoadingSpinner } from './components/LoadingSpinner';

interface Product {
    id: number;
    name: string;
    price: number;
    description?: string;
    category?: string;
}

export default function HomePage() {
    const [products, setProducts] = useState<Product[]>([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [productsError, setProductsError] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);

    // Check if user is logged in
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    setUser(JSON.parse(storedUser));
                } catch (e) {
                    console.error('Failed to parse user', e);
                }
            }
        }
    }, []);

    useEffect(() => {
        // Fetch real products from API
        setProductsLoading(true);
        setProductsError(null);

        fetch(`${API_BASE_URL}/products`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch products');
                return res.json();
            })
            .then(data => {
                const productList = data.products || data || [];
                setProducts(Array.isArray(productList) ? productList.slice(0, 4) : []);
                setProductsLoading(false);
            })
            .catch(err => {
                console.error('Failed to load products:', err);
                setProductsError('Unable to load featured products. Please try again later.');
                setProductsLoading(false);
                // Fallback to empty array
                setProducts([]);
            });
    }, []);

    // VULNERABILITY: DOM-based XSS - reading from URL parameters and displaying unsafely
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Read from URL search params (e.g., /?welcome=<script>alert(1)</script>)
            const urlParams = new URLSearchParams(window.location.search);
            const welcomeMsg = urlParams.get('welcome');
            if (welcomeMsg) {
                const welcomeDiv = document.getElementById('welcome-message');
                if (welcomeDiv) {
                    welcomeDiv.innerHTML = welcomeMsg; // VULNERABILITY: Unsafe innerHTML
                }
            }

            // Also read from localStorage (could be set by other pages)
            const userGreeting = localStorage.getItem('userGreeting');
            if (userGreeting) {
                const greetingDiv = document.getElementById('user-greeting');
                if (greetingDiv) {
                    greetingDiv.innerHTML = userGreeting; // VULNERABILITY: Unsafe innerHTML
                }
            }
        }
    }, []);

    return (
        <div className="min-h-screen">
            {/* Hero Section - C001: Enhanced Hero with animations and better UX */}
            <section className="relative overflow-hidden pt-20 pb-32">
                {/* Animated background gradients */}
                <div className="absolute top-20 left-1/3 w-[500px] h-[500px] bg-orange-500/20 rounded-full blur-[150px] animate-pulse-slow" />
                <div className="absolute top-40 right-1/4 w-[400px] h-[400px] bg-orange-600/10 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '1s' }} />

                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    {/* VULNERABILITY: DOM-based XSS - displaying unsanitized content from URL params */}
                    <div id="welcome-message" className="text-sm text-orange-400 mb-2"></div>

                    {/* VULNERABILITY: DOM-based XSS - displaying unsanitized content from localStorage */}
                    <div id="user-greeting" className="text-sm text-blue-400 mb-2"></div>

                    {/* Badge with animation */}
                    <div className="badge-primary inline-flex items-center gap-2 mb-8 animate-slide-up">
                        <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                        New Collection 2026
                    </div>

                    {/* Main heading with gradient */}
                    <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight animate-slide-up" style={{ animationDelay: '0.1s' }}>
                        <span className="text-white block">Shop the</span>
                        <span className="text-gradient-primary block">Future Today</span>
                    </h1>

                    {/* Subtitle */}
                    <p className="text-lg sm:text-xl text-neutral-400 max-w-2xl mx-auto mb-12 leading-relaxed animate-slide-up" style={{ animationDelay: '0.2s' }}>
                        Discover premium products with unbeatable prices. Fast shipping worldwide,
                        secure payments, and amazing customer service.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
                        <Link href="/products" className="btn-primary text-lg px-10 py-4 inline-flex items-center justify-center gap-2 group">
                            Browse Products
                            <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </Link>
                        {user ? (
                            <Link href="/profile" className="btn-secondary text-lg px-10 py-4 inline-flex items-center justify-center">
                                My Account
                            </Link>
                        ) : (
                            <Link href="/login" className="btn-secondary text-lg px-10 py-4 inline-flex items-center justify-center">
                                Login / Register
                            </Link>
                        )}
                    </div>

                    {/* Trust indicators */}
                    <div className="mt-16 flex flex-wrap justify-center gap-8 text-sm text-neutral-500 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Free Shipping
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v8m0-8c1.11 0 2.08.402 2.599 1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Secure Payment
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            24/7 Support
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="relative max-w-7xl mx-auto px-4 py-20">
                <div className="flex justify-between items-end mb-12">
                    <div>
                        <span className="badge-primary mb-3 inline-block">Trending</span>
                        <h2 className="text-3xl md:text-4xl font-bold">Featured Products</h2>
                    </div>
                    <Link href="/products" className="text-orange-400 hover:text-orange-300 font-medium hidden md:flex items-center gap-2">
                        View All →
                    </Link>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {productsLoading ? (
                        <div className="col-span-4 flex justify-center py-12">
                            <LoadingSpinner size="lg" text="Loading featured products..." />
                        </div>
                    ) : productsError ? (
                        <div className="col-span-4 text-center py-12">
                            <p className="text-rose-400 mb-4">{productsError}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn-secondary"
                            >
                                Retry
                            </button>
                        </div>
                    ) : products.length > 0 ? (
                        products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                showDescription={true}
                                className="card-hover"
                            />
                        ))
                    ) : (
                        <div className="col-span-4 text-center py-12 text-neutral-400">
                            <p>No featured products available at the moment.</p>
                            <Link href="/products" className="btn-primary mt-4 inline-block">
                                Browse All Products
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* Stats Section */}
            <section className="relative max-w-7xl mx-auto px-4 py-20">
                <div className="grid md:grid-cols-4 gap-6">
                    {[
                        { label: 'Happy Customers', value: '10K+', icon: '👥' },
                        { label: 'Products', value: '500+', icon: '📦' },
                        { label: 'Orders Delivered', value: '50K+', icon: '🚚' },
                        { label: 'Countries', value: '100+', icon: '🌍' },
                    ].map((stat, idx) => (
                        <div
                            key={idx}
                            className="card text-center animate-fade-in"
                            style={{ animationDelay: `${idx * 0.1}s` }}
                        >
                            <div className="text-4xl mb-3">{stat.icon}</div>
                            <div className="text-3xl font-bold text-gradient-primary mb-2">{stat.value}</div>
                            <div className="text-sm text-neutral-400">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section className="relative max-w-7xl mx-auto px-4 py-20">
                <div className="text-center mb-12">
                    <span className="badge-primary mb-3 inline-block">Why Choose Us</span>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">Features You'll Love</h2>
                    <p className="text-neutral-400 max-w-2xl mx-auto">
                        Everything you need for a seamless shopping experience
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        {
                            icon: '🔒',
                            title: 'Secure Payments',
                            description: 'Multiple payment options with industry-standard encryption',
                        },
                        {
                            icon: '📦',
                            title: 'Fast Shipping',
                            description: 'Free shipping on orders over $50. Express delivery available.',
                        },
                        {
                            icon: '↩️',
                            title: 'Easy Returns',
                            description: '30-day return policy. No questions asked.',
                        },
                        {
                            icon: '💬',
                            title: '24/7 Support',
                            description: 'Round-the-clock customer service via chat, email, or phone.',
                        },
                        {
                            icon: '⭐',
                            title: 'Quality Guaranteed',
                            description: 'All products are quality-checked before shipping.',
                        },
                        {
                            icon: '🎁',
                            title: 'Loyalty Rewards',
                            description: 'Earn points with every purchase and redeem for discounts.',
                        },
                    ].map((feature, idx) => (
                        <div
                            key={idx}
                            className="card p-6 hover:border-orange-500/30 transition-all animate-scale-in"
                            style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                            <div className="text-4xl mb-4">{feature.icon}</div>
                            <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                            <p className="text-neutral-400 text-sm">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Banner */}
            <section className="relative max-w-7xl mx-auto px-4 py-20">
                <div className="relative rounded-3xl overflow-hidden bg-orange-600">
                    <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />

                    <div className="relative px-8 md:px-16 py-16 text-center">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Get 20% Off Your First Order
                        </h2>
                        <p className="text-white/80 text-lg max-w-xl mx-auto mb-8">
                            Subscribe to our newsletter and receive exclusive deals and insider-only discounts.
                        </p>
                        <form
                            className="flex flex-col sm:flex-row max-w-md mx-auto gap-3"
                            onSubmit={(e) => {
                                e.preventDefault();
                                // VULNERABILITY: No CSRF protection, email stored in localStorage without validation
                                // VULNERABILITY: Client-side only validation, can be bypassed
                                const emailInput = e.currentTarget.querySelector('input[type="email"]') as HTMLInputElement;
                                const email = emailInput?.value;
                                if (email) {
                                    // VULNERABILITY: Storing email in localStorage (sensitive data exposure)
                                    localStorage.setItem('newsletter_email', email);
                                    // VULNERABILITY: No server-side validation or sanitization
                                    alert('Thank you for subscribing! (Demo only - no actual subscription)');
                                    emailInput.value = '';
                                }
                            }}
                        >
                            <input
                                type="email"
                                placeholder="Enter your email"
                                className="flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 placeholder-white/60 text-white outline-none focus:border-white/40"
                                required
                            />
                            <button
                                type="submit"
                                className="px-8 py-4 rounded-xl bg-black text-white font-bold hover:bg-neutral-900 transition-colors"
                            >
                                Subscribe
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
